import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService, { API_BASE_URL } from '../services/authService';
import { CircularProgress, Box } from '@mui/material';
import api from '../services/api'; // Importar a instância configurada do axios

interface AuthContextType {
  user: any;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext < AuthContextType | undefined > (undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState < any > (null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const login = async (username: string, password: string) => {
    if (!username || !password) {
      // Aqui podemos lançar um erro ou apenas retornar sem enviar request
      console.warn('Usuário ou senha não podem estar vazios');
      return;
    }

    const res = await authService.login(username, password);
    if (res.token) {
      localStorage.setItem('token', res.token);
    }
    setUser(res.user);

    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  };


  const logout = useCallback(() => {
    authService.logout();
    setUser(null);

    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = authService.getToken();
      const storedUser = authService.getCurrentUser();

      if (!storedToken || !storedUser) {
        setUser(null);
      } else {
        try {

          setUser(storedUser);
        } catch {
          setUser(null);
          authService.logout();
        }
      }

      setLoading(false); // sempre termina o loading
    };

    initializeAuth();
  }, []);




  // Interceptor para tokens expirados
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && error.response?.config?.url !== `${API_BASE_URL}/login` && !error.config._retry) {
          error.config._retry = true;
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return context;
};
